import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ProductListPage from "./pages/ProductListPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PromotionsPage from "./pages/PromotionsPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import LargeFormatHome from "./pages/LargeFormat/LargeFormatHome";
import LargeFormatProducts from "./pages/LargeFormat/LargeFormatProducts";
import LargeFormatProductDetail from "./pages/LargeFormat/LargeFormatProductDetail";
import SmallFormatHome from "./pages/SmallFormat/SmallFormatHome";
import SmallFormatProducts from "./pages/SmallFormat/SmallFormatProducts";
import SmallFormatProductDetail from "./pages/SmallFormat/SmallFormatProductDetail";
import SwagHome from "./pages/Swag/SwagHome";
import SwagProducts from "./pages/Swag/SwagProducts";
import SwagProductDetail from "./pages/Swag/SwagProductDetail";
import AwardsHome from "./pages/Awards/AwardsHome";
import AwardsProducts from "./pages/Awards/AwardsProducts";
import AwardsProductDetail from "./pages/Awards/AwardsProductDetail";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SetupAccountPage from "./pages/SetupAccountPage";
import AuthCallback from "./pages/AuthCallback";
import AdminDashboard from "./pages/AdminDashboard";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import CustomerOrderDetailPage from "./pages/CustomerOrderDetailPage";
import MyAccountPage from "./pages/MyAccountPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import ProductDetailV2 from "./pages/ProductDetailV2";
import ProductsListV2 from "./pages/ProductsListV2";

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Only scroll to top if there's no hash (anchor link)
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, hash]);

  return null;
};

// Layout wrapper to conditionally show Navbar/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const hideLayout = ['/login', '/register', '/auth/callback', '/admin', '/verify-email', '/forgot-password', '/reset-password'].some(
    path => location.pathname.startsWith(path)
  );

  return (
    <>
      {!hideLayout && <Navbar />}
      {children}
      {!hideLayout && <Footer />}
    </>
  );
};

function App() {
  return (
    <SiteSettingsProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <div className="App">
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductListPage />} />
                  <Route path="/products/:categoryId" element={<ProductListPage />} />
                  <Route path="/product/:productId" element={<ProductDetailPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/promotions" element={<PromotionsPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/track-order" element={<OrderTrackingPage />} />
                  <Route path="/my-orders" element={<MyOrdersPage />} />
                  <Route path="/my-orders/:orderId" element={<CustomerOrderDetailPage />} />
                  
                  {/* Large Format Routes */}
                  <Route path="/LargePrint" element={<LargeFormatHome />} />
                  <Route path="/LargePrint/products" element={<LargeFormatProducts />} />
                  <Route path="/LargePrint/products/:categoryId" element={<LargeFormatProducts />} />
                  <Route path="/LargePrint/product/:productId" element={<LargeFormatProductDetail />} />
                  
                  {/* Small Format Routes */}
                  <Route path="/SmallPrint" element={<SmallFormatHome />} />
                  <Route path="/SmallPrint/products" element={<SmallFormatProducts />} />
                  <Route path="/SmallPrint/products/:categoryId" element={<SmallFormatProducts />} />
                  <Route path="/SmallPrint/product/:productId" element={<SmallFormatProductDetail />} />
                  
                  {/* Promotional Swag Routes */}
                  <Route path="/Swag" element={<SwagHome />} />
                  <Route path="/Swag/products" element={<SwagProducts />} />
                  <Route path="/Swag/products/:categoryId" element={<SwagProducts />} />
                  <Route path="/Swag/product/:productId" element={<SwagProductDetail />} />
                  
                  {/* Awards Routes */}
                  <Route path="/Awards" element={<AwardsHome />} />
                  <Route path="/Awards/products" element={<AwardsProducts />} />
                  <Route path="/Awards/products/:categoryId" element={<AwardsProducts />} />
                  <Route path="/Awards/product/:productId" element={<AwardsProductDetail />} />
                  
                  {/* V2 Product Routes (New Template-Based Architecture) */}
                  <Route path="/v2/products" element={<ProductsListV2 />} />
                  <Route path="/v2/products/:productType" element={<ProductsListV2 />} />
                  <Route path="/v2/product/:productId" element={<ProductDetailV2 />} />
                  
                  {/* Legal & Info Pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  
                  {/* Auth Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/setup-account" element={<SetupAccountPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<EmailVerificationPage />} />
                  
                  {/* User Account Routes */}
                  <Route path="/my-account" element={<MyAccountPage />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                </Routes>
              </Layout>
              <Toaster />
            </div>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </SiteSettingsProvider>
  );
}

export default App;
